"use client";
import { Carousel } from "@zapier/design-system-beta";
import { Icon } from "@zapier/zinnia-icons";
import { Divider, Button, Text, LegacyButton } from "@zapier/design-system";
import { ZapCard } from "../ZapCard/ZapCard";

import { useRouter } from "next/navigation";
import styles from "./TemplateDetailPage.module.css";
import { Markdown } from "./Markdown";
import { getBreadcrumbs } from "./constants";
import { TemplateDetailPageHeader } from "./TemplateDetailPageHeader";
import { BreadCrumbs } from "../BreadCrumbs/BreadCrumbs";
import { CanvasEmbed } from "../CanvasEmbed/CanvasEmbed";
import { FAQSchema } from "./FAQSchema";
import {
  emitUserInteractionEvent,
  UserInteractionEventName,
} from "src/observability/tracking";
import {
  type Template,
  AppsSection,
  CreatedAssetsSection,
  TemplateCTAs,
} from "@zapier/templates";
import { useTemplateExecution } from "src/contexts/TemplateExecutionContext";
import {
  TemplateBookmarkProvider,
  useTemplateBookmark,
} from "src/contexts/TemplateBookmarkContext";

type TemplateDetailPageProps = {
  template: Template;
  relatedTemplates: Template[];
  isDraftModeEnabled: boolean;
  isLoggedIn: boolean;
};

export const TemplateDetailPage = ({
  template,
  relatedTemplates,
  isDraftModeEnabled,
  isLoggedIn,
}: TemplateDetailPageProps) => {
  const router = useRouter();

  const {
    createUserTemplateBookmark,
    deleteUserTemplateBookmark,
    bookmarkedTemplates,
    templateBookmarkChanging,
  } = useTemplateBookmark();

  const {
    createTemplate,
    creationScreen,
    error: creationError,
    setError: setCreationError,
  } = useTemplateExecution();

  const tryTemplateActions = {
    hasCreationScreen: !!creationScreen,
    createTemplate: createTemplate,
    creationError: creationError,
    setCreationError: setCreationError,
  };

  const canvasEmbedSection = template.canvasUrl ? (
    <section className={styles.hiddenOnMobile}>
      <div className={styles.canvasTitleSection}>
        <Text type="SectionHeader" tag="h2">
          Workflow preview:
        </Text>
        <LegacyButton
          color="tertiary"
          iconBefore="navCanvas"
          href={template.canvasUrl}
          onClick={() => {
            emitUserInteractionEvent(
              UserInteractionEventName.openInCanvasButtonClicked,
              {
                event_action: "click",
                option_selected: template.slug,
              },
            );
          }}
        >
          Open in Canvas
        </LegacyButton>
      </div>
      <Text type="Body1Multiline" margin="0 0 12px 0" tag="p">
        Click and drag to move around the canvas and pinch to zoom.
      </Text>
      <CanvasEmbed
        url={template.canvasUrl}
        showOpenInCanvasButton={false}
        className={styles.canvasEmbed}
      />
    </section>
  ) : null;

  const overviewSection = template.content ? (
    <section>
      <Text type="SectionHeader" tag="h2">
        Overview
      </Text>
      <Markdown content={template.contentAsMarkdown ?? ""} />
    </section>
  ) : null;

  const faqSection = template.faqs && template.faqs?.length > 0 && (
    <section>
      <Text type="SectionHeader" tag="h2">
        Frequently asked questions
      </Text>
      <div className={styles.faqs}>
        {template.faqs?.map((faq) => (
          <details key={faq.name} className={styles.faqItem}>
            <summary className={styles.faqItemSummary}>
              <Text type="ParagraphHeader3">{faq.name}</Text>
              <div className={styles.faqItemIcon}>
                <Icon name="arrowSmallDown" size={24} isBlock />
              </div>
            </summary>
            <Markdown content={faq.content} />
          </details>
        ))}
      </div>
    </section>
  );

  const ctaSection = !isLoggedIn && (
    <section className={styles.ctaBanner}>
      <Text tag="h2" type="PageHeader" className={styles.title}>
        {template.name}
      </Text>
      <TemplateCTAs
        template={template}
        isLoggedIn={isLoggedIn}
        align="center"
        hasBookmarking={isLoggedIn}
        templateBookmarkActions={{
          onSave: createUserTemplateBookmark,
          onUnsave: deleteUserTemplateBookmark,
          isBookmarked: Boolean(
            template.id && bookmarkedTemplates?.includes(template.id),
          ),
          isLoading: templateBookmarkChanging === template.id,
        }}
        tryTemplateActions={tryTemplateActions}
        hasTemplatePage={true}
      />
    </section>
  );

  const relatedTemplatesSection = relatedTemplates.length > 0 && (
    <section>
      <Text type="SectionHeader" tag="h2" margin="0 0 12px 0">
        Related templates
      </Text>
      <div className={styles.relatedTemplates}>
        <Carousel
          className={styles.carouselWrapper}
          paddingBottom={30}
          pageGutterWidth={0}
        >
          {relatedTemplates.map((template) => (
            <ZapCard
              key={template.slug}
              template={template}
              isLoggedIn={isLoggedIn}
              hasBookmarking={isLoggedIn}
              href={`/templates/details/${template.slug}`}
            />
          ))}
        </Carousel>
      </div>
    </section>
  );

  const page = (
    <div className={styles.page}>
      {!isLoggedIn && <FAQSchema questions={template.faqs ?? null} />}
      {isLoggedIn ? (
        <Button
          iconPosition="start"
          variant="ghost"
          onPress={() => router.back()}
        >
          <Icon name="arrowSmallLeft" size={20} /> Back
        </Button>
      ) : (
        <BreadCrumbs crumbs={getBreadcrumbs(template)} />
      )}
      <TemplateDetailPageHeader
        template={template}
        isLoggedIn={isLoggedIn}
        isDraftModeEnabled={isDraftModeEnabled}
      />
      <Divider height={1} width="full" />
      <div className={styles.layout}>
        <div className={styles.mainContent}>
          {canvasEmbedSection}
          {overviewSection}
          {faqSection}
          {ctaSection}
          {relatedTemplatesSection}
        </div>
        <aside className={styles.sidebar}>
          <CreatedAssetsSection template={template} />
          <AppsSection template={template} />
        </aside>
      </div>
    </div>
  );

  if (isLoggedIn) {
    return (
      <TemplateBookmarkProvider
        bookmarkedTemplates={[]}
        createUserTemplateBookmark={createUserTemplateBookmark}
        deleteUserTemplateBookmark={deleteUserTemplateBookmark}
        templateBookmarkChanging={null}
      >
        {page}
      </TemplateBookmarkProvider>
    );
  }

  return page;
};
